import {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import CreateReportBox, {PLAIN_TOOLTIP} from './CreateReport/CreateReportBox';
import {useGetGroupFiltersQuery} from '../../../api/services/groupService';
import {deleteSavedReport, getSavedReports} from '../../../api/services/savedReportsService';
import {useConfirm} from 'material-ui-confirm';
import useUser from '../../../utility/hooks/useUser';
import {REPORT_ACCESS_LINE} from '../../../utility/reportFieldAccess';
import {aboutSavedReport, availableTo, lastRun} from './CreateReport/savedReportText';

const BLUE_BUTTON = {backgroundColor: '#186BBE', boxShadow: 'none', '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}};

// AD-2699: every report, regular and medical, is made in the Create Report box, in the same way as forms.
// Saved reports (AD-2700, AD-2701) are listed under the button, each with 'Run', 'Edit' and 'Delete'.
function ReportsTab() {
    const {user} = useUser();
    const isParentRole = user.isParent();
    const [box, setBox] = useState({open: false, mode: 'create', savedReport: null});
    const [savedReports, setSavedReports] = useState([]);
    const confirm = useConfirm();

    const {data: groupFilters = {modules: []}} = useGetGroupFiltersQuery(
        undefined,
        {skip: isParentRole},
    );

    const loadSavedReports = useCallback(async () => {
        try {
            setSavedReports(await getSavedReports());
        } catch {
            setSavedReports([]);
        }
    }, []);

    useEffect(() => {
        if (!isParentRole) loadSavedReports();
    }, [isParentRole, loadSavedReports]);

    const deleteReport = async (report) => {
        try {
            await confirm({
                title: 'Confirm Deletion',
                description: (
                    <>
                        Are you sure you want to delete this report?
                        <br/><br/>
                        <strong>{report.name}</strong>
                    </>
                ),
                confirmationText: 'Delete',
                cancellationText: 'Cancel',
            });
        } catch {
            return;
        }
        await deleteSavedReport(report.id);
        loadSavedReports();
    };

    const closeBox = () => {
        setBox({open: false, mode: 'create', savedReport: null});
        loadSavedReports();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                mt: 2,
                overflowY: 'auto',
            }}
        >
            <Box sx={{mb: 2}}>
                {/* Maria, 15 October 2026: the Manage Access line is the button's hover pop-up. */}
                <Tooltip
                    title={<span data-testid="report-access-line">{REPORT_ACCESS_LINE}</span>}
                    placement="right"
                    // Maria, 15 October 2026: ordinary text, not the grey pop-up box.
                    // the line wraps into two lines of even length
                    componentsProps={{tooltip: {sx: {...PLAIN_TOOLTIP.tooltip.sx, maxWidth: 420}}}}
                >
                    <Button
                        variant="contained"
                        data-testid="create-new-report"
                        onClick={() => setBox({open: true, mode: 'create', savedReport: null})}
                        sx={BLUE_BUTTON}
                    >
                        Create New Report
                    </Button>
                </Tooltip>
            </Box>

            {savedReports.length > 0 && (
                <Box data-testid="saved-reports">
                    <Typography variant="h6" sx={{fontWeight: 600, mb: 1}}>Saved Reports</Typography>
                    <TableContainer sx={{border: '1px solid #e3e8ef', borderRadius: 1}}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 600}}>Report</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>About</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Saved by</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Available to</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Last run</TableCell>
                                    <TableCell/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {savedReports.map(report => (
                                    <TableRow key={report.id} data-testid={`saved-report-${report.id}`} sx={{'&:last-child td': {borderBottom: 0}}}>
                                        <TableCell sx={{fontWeight: 500}}>{report.name}</TableCell>
                                        <TableCell>{aboutSavedReport(report)}</TableCell>
                                        <TableCell>{report.createdByName}</TableCell>
                                        <TableCell>{availableTo(report)}</TableCell>
                                        <TableCell>{lastRun(report)}</TableCell>
                                        {/* Maria, 15 October 2026: 'Run', 'Edit', 'Delete', in that order, so they take up less space. */}
                                        <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                data-testid={`run-saved-report-${report.id}`}
                                                onClick={() => setBox({open: true, mode: 'run', savedReport: report})}
                                                sx={BLUE_BUTTON}
                                            >
                                                Run
                                            </Button>
                                            {report.canEdit && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    data-testid={`edit-saved-report-${report.id}`}
                                                    onClick={() => setBox({open: true, mode: 'edit', savedReport: report})}
                                                    sx={{ml: 1}}
                                                >
                                                    Edit
                                                </Button>
                                            )}
                                            {report.canEdit && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    data-testid={`delete-saved-report-${report.id}`}
                                                    onClick={() => deleteReport(report)}
                                                    sx={{ml: 1}}
                                                >
                                                    Delete
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            <CreateReportBox
                open={box.open}
                mode={box.mode}
                savedReport={box.savedReport}
                groupFilters={groupFilters.modules}
                onClose={closeBox}
                onSaved={loadSavedReports}
            />
        </Box>
    );
}

export default ReportsTab;
